import { Subject } from 'rxjs';
import { ANY_OPTION, getNextRangeState, isOptionInRange } from 'utils/smartRangeSelection';

const OPTION_CLASS = 'nhs-c-number-select__option';
const ACTIVE_CLASS = `${OPTION_CLASS}--active`;
const DEFAULT_STATE = { min: null, max: null };

/**
 * Generic number select control (Figma "Number select"): a row of Any/1/2/3/4/5+-style buttons
 * implementing the shared Smart Range Selection interaction (`utils/smartRangeSelection`) —
 * clicking a number highlights it and every higher option; further clicks extend, trim or narrow
 * that selection to a range or an exact value depending on where they fall (see
 * `getNextRangeState` for the full rules). "Any" always resets to the default state.
 *
 * The component never creates DOM: `getHtml` only returns the markup as a string, and the calling
 * control turns it into an element and hands that element to the constructor, which binds the
 * behavior. It manages the highlighted-buttons state via a plain click listener — not the exposed
 * observable, since the visual state must keep working whether or not anyone subscribes — and
 * separately emits the new `{ min, max }` state on `getObservable()`.
 *
 * `labelledBy` is the id of the element that names the group (e.g. the "Bedrooms" label), so
 * assistive technology announces what the highlighted buttons represent.
 */
export default class NumberSelect {
  static getSelector() {
    return '[data-number-select]';
  }

  static getHtml({ options, automationIdentifier, labelledBy }) {
    const optionsHtml = options
      .map((option) => `
        <button type="button"
                class="${OPTION_CLASS}${option === ANY_OPTION ? ` ${ACTIVE_CLASS}` : ''}"
                aria-pressed="${option === ANY_OPTION}"
                data-value="${option}"
                ${automationIdentifier ? `data-qa="${automationIdentifier}_option_${option.toLowerCase()}"` : ''}>
          ${option}
        </button>
      `)
      .join('');

    return `
      <div class="nhs-c-number-select"
           role="group"
           data-number-select
           ${labelledBy ? `aria-labelledby="${labelledBy}"` : ''}
           ${automationIdentifier ? `data-qa="${automationIdentifier}"` : ''}>
        ${optionsHtml}
      </div>
    `;
  }

  constructor(element) {
    this.element = element;
    this.state = DEFAULT_STATE;
    this.value$ = new Subject();

    this.onClick = (event) => {
      const button = event.target.closest('[data-value]');
      if (!button) {
        return;
      }

      this.setState(getNextRangeState(this.state, button.dataset.value));
      this.value$.next(this.state);
    };
    this.element.addEventListener('click', this.onClick);
  }

  setState(state) {
    this.state = state;
    this.element.querySelectorAll('[data-value]').forEach((button) => {
      const isActive = isOptionInRange(button.dataset.value, state);
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  getObservable() {
    return this.value$.asObservable();
  }

  destroy() {
    this.element.removeEventListener('click', this.onClick);
  }
}
